<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, no-await-in-loop */
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useConversationStages } from 'dashboard/composables/useConversationStages';
import {
  CONVERSATION_STAGE_AND_LEGACY_LABELS,
  LEGACY_CONVERSATION_STAGE_LABEL_MAP,
  formatConversationStageLabel,
  getConversationStageOrder,
  isConversationStageLabel,
  normalizeConversationStageLabel,
} from 'dashboard/constants/conversationStages';
import ConversationAPI from 'dashboard/api/conversations';
import wootConstants from 'dashboard/constants/globals';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const router = useRouter();
const { stageOptions } = useConversationStages();
const accountLabels = useMapGetter('labels/getLabels');

const conversations = ref([]);
const isLoading = ref(false);
const draggedConversationId = ref(null);
const activeDropStage = ref('');
const searchQuery = ref('');
const selectedAgent = ref('all');
const selectedLabel = ref('all');
const selectedActivity = ref('all');
const unreadOnly = ref(false);

const fallbackStageColors = [
  '#2563eb',
  '#7c3aed',
  '#0891b2',
  '#ea580c',
  '#16a34a',
  '#dc2626',
  '#4f46e5',
];

const loadedCount = computed(() => conversations.value.length);

const stageLabelRecords = computed(() =>
  accountLabels.value.filter(isConversationStageLabel)
);

const regularLabelRecords = computed(() =>
  accountLabels.value.filter(label => !isConversationStageLabel(label))
);

const stageOrderFor = stageId => {
  if (stageId === 'nunca-respondeu') return -1;

  const label = stageLabelRecords.value.find(
    item => normalizeConversationStageLabel(item.title) === stageId
  );
  return getConversationStageOrder(label || stageId);
};

const orderedStageOptions = computed(() =>
  [...stageOptions.value].sort(
    (a, b) =>
      stageOrderFor(a.id) - stageOrderFor(b.id) || a.id.localeCompare(b.id)
  )
);

const stageColorMap = computed(() => {
  return orderedStageOptions.value.reduce((map, stage, index) => {
    const label = stageLabelRecords.value.find(
      item => normalizeConversationStageLabel(item.title) === stage.id
    );
    map[stage.id] =
      label?.color || fallbackStageColors[index % fallbackStageColors.length];
    return map;
  }, {});
});

const fetchConversations = async () => {
  const conversationsById = new Map();
  let page = 1;
  let totalCount = 0;
  let shouldKeepLoading = true;
  const maxPages = 100;

  isLoading.value = true;

  try {
    while (shouldKeepLoading && page <= maxPages) {
      const {
        data: { data },
      } = await ConversationAPI.get({
        assigneeType: wootConstants.ASSIGNEE_TYPE.ALL,
        status: wootConstants.STATUS_TYPE.ALL,
        sortBy: wootConstants.SORT_BY_TYPE.LAST_MESSAGE_AT_DESC,
        page,
      });

      const payload = data?.payload || [];
      payload.forEach(conversation => {
        conversationsById.set(conversation.id, conversation);
      });

      totalCount = Number(data?.meta?.all_count || totalCount);
      shouldKeepLoading =
        payload.length > 0 &&
        (!totalCount || conversationsById.size < totalCount);
      page += 1;
    }

    conversations.value = [...conversationsById.values()];
  } catch (error) {
    useAlert('Não foi possível carregar todas as conversas do kanban');
  } finally {
    isLoading.value = false;
  }
};

const currentStageFor = conversation => {
  const labels = conversation.labels || [];
  return (
    stageOptions.value.find(stage => labels.includes(stage.id))?.id ||
    Object.entries(LEGACY_CONVERSATION_STAGE_LABEL_MAP).find(([legacyLabel]) =>
      labels.includes(legacyLabel)
    )?.[1] ||
    stageOptions.value[0]?.id
  );
};

const conversationName = conversation =>
  conversation.meta?.sender?.name || `Conversa #${conversation.id}`;

const conversationAgent = conversation =>
  conversation.meta?.assignee?.name || 'Sem agente';

const conversationTimestamp = conversation =>
  conversation.last_activity_at || conversation.timestamp || 0;

const relativeActivityLabel = timestamp => {
  if (!timestamp) return 'Sem atividade';

  const diffInSeconds = Math.max(0, Date.now() / 1000 - Number(timestamp));
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 1) return 'Agora';
  if (diffInMinutes < 60) return `${diffInMinutes} min`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours} h`;

  const diffInDays = Math.floor(diffInHours / 24);
  return `${diffInDays} d`;
};

const activityBucket = conversation => {
  const timestamp = conversationTimestamp(conversation);
  if (!timestamp) return 'old';

  const diffInHours = (Date.now() / 1000 - Number(timestamp)) / 3600;
  if (diffInHours <= 24) return 'today';
  if (diffInHours <= 72) return 'recent';
  return 'old';
};

const agentOptions = computed(() => {
  const agents = new Map();
  conversations.value.forEach(conversation => {
    const assignee = conversation.meta?.assignee;
    if (assignee?.id) agents.set(String(assignee.id), assignee.name);
  });

  return [...agents.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
});

const usedLabelOptions = computed(() => {
  const usedLabels = new Set(
    conversations.value.flatMap(item => item.labels || [])
  );
  return regularLabelRecords.value
    .filter(label => usedLabels.has(label.title))
    .sort((a, b) => a.title.localeCompare(b.title));
});

const visibleConversations = computed(() => {
  const query = searchQuery.value.trim().toLowerCase();

  return conversations.value.filter(conversation => {
    const labels = conversation.labels || [];
    const name = conversationName(conversation).toLowerCase();
    const agent = conversationAgent(conversation).toLowerCase();
    const matchesQuery =
      !query ||
      name.includes(query) ||
      agent.includes(query) ||
      labels.some(label => label.toLowerCase().includes(query));
    const matchesAgent =
      selectedAgent.value === 'all' ||
      String(conversation.meta?.assignee?.id || 'unassigned') ===
        selectedAgent.value;
    const matchesLabel =
      selectedLabel.value === 'all' || labels.includes(selectedLabel.value);
    const matchesUnread = !unreadOnly.value || conversation.unread_count > 0;
    const matchesActivity =
      selectedActivity.value === 'all' ||
      activityBucket(conversation) === selectedActivity.value;

    return (
      matchesQuery &&
      matchesAgent &&
      matchesLabel &&
      matchesUnread &&
      matchesActivity
    );
  });
});

const conversationsByStage = computed(() => {
  const map = Object.fromEntries(
    orderedStageOptions.value.map(stage => [stage.id, []])
  );
  visibleConversations.value.forEach(conversation => {
    const currentStage = currentStageFor(conversation);
    if (currentStage) map[currentStage].push(conversation);
  });
  return map;
});

const kanbanStats = computed(() => {
  const unassigned = visibleConversations.value.filter(
    item => !item.meta?.assignee
  ).length;
  const unread = visibleConversations.value.reduce(
    (sum, item) => sum + Number(item.unread_count || 0),
    0
  );
  const today = visibleConversations.value.filter(
    item => activityBucket(item) === 'today'
  ).length;

  return {
    total: visibleConversations.value.length,
    unread,
    unassigned,
    today,
  };
});

const stageMetrics = stageId => {
  const items = conversationsByStage.value[stageId] || [];
  return {
    total: items.length,
    unread: items.reduce(
      (sum, item) => sum + Number(item.unread_count || 0),
      0
    ),
    unassigned: items.filter(item => !item.meta?.assignee).length,
  };
};

const funnelTotal = computed(() => visibleConversations.value.length || 0);

const stagePercentFor = stageId => {
  if (!funnelTotal.value) return 0;
  return Math.round((stageMetrics(stageId).total / funnelTotal.value) * 100);
};

const cardLabelsFor = conversation => {
  const labels = conversation.labels || [];
  return regularLabelRecords.value
    .filter(label => labels.includes(label.title))
    .slice(0, 3);
};

const resetFilters = () => {
  searchQuery.value = '';
  selectedAgent.value = 'all';
  selectedLabel.value = 'all';
  selectedActivity.value = 'all';
  unreadOnly.value = false;
};

const openConversation = conversation => {
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversation.id },
  });
};

const moveConversation = async stageId => {
  const conversation = conversations.value.find(
    item => item.id === draggedConversationId.value
  );
  if (!conversation || !stageId) return;

  const otherLabels = (conversation.labels || []).filter(
    label =>
      !stageOptions.value.some(stage => stage.id === label) &&
      !CONVERSATION_STAGE_AND_LEGACY_LABELS.includes(label)
  );
  const labels = [...otherLabels, stageId];

  try {
    const { data } = await ConversationAPI.updateLabels(
      conversation.id,
      labels
    );
    const updatedConversation = {
      ...conversation,
      labels: data.payload,
    };
    conversations.value = conversations.value.map(item =>
      item.id === updatedConversation.id ? updatedConversation : item
    );
    store.dispatch('updateConversation', updatedConversation);
    useAlert(`Movido para ${formatConversationStageLabel(stageId)}`);
  } catch (error) {
    useAlert('Não foi possível mover a conversa');
  } finally {
    draggedConversationId.value = null;
    activeDropStage.value = '';
  }
};

onMounted(fetchConversations);
</script>

<template>
  <main class="flex flex-col flex-1 min-w-0 h-full bg-n-background">
    <header
      class="flex flex-col gap-2 px-4 py-3 border-b border-solid border-n-weak bg-n-surface-1"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h1 class="m-0 text-xl font-semibold text-n-slate-12">Kanban</h1>
          <p class="m-0 mt-1 text-sm text-n-slate-11">
            Arraste clientes entre etapas para atualizar o atendimento.
            <span v-if="loadedCount" class="text-n-slate-10">
              {{ loadedCount }} conversas carregadas.
            </span>
          </p>
        </div>
        <Button
          label="Atualizar"
          icon="i-lucide-refresh-cw"
          slate
          size="sm"
          :is-loading="isLoading"
          @click="fetchConversations"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <div
          class="flex items-center gap-2 h-8 px-2.5 border border-solid rounded-md border-n-weak bg-n-alpha-1"
        >
          <p class="m-0 text-xs text-n-slate-10">Em visão</p>
          <p class="m-0 text-sm font-semibold text-n-slate-12">
            {{ kanbanStats.total }}
          </p>
        </div>
        <div
          class="flex items-center gap-2 h-8 px-2.5 border border-solid rounded-md border-n-weak bg-n-alpha-1"
        >
          <p class="m-0 text-xs text-n-slate-10">Mensagens</p>
          <p class="m-0 text-sm font-semibold text-n-teal-10">
            {{ kanbanStats.unread }}
          </p>
        </div>
        <div
          class="flex items-center gap-2 h-8 px-2.5 border border-solid rounded-md border-n-weak bg-n-alpha-1"
        >
          <p class="m-0 text-xs text-n-slate-10">Sem agente</p>
          <p class="m-0 text-sm font-semibold text-n-amber-10">
            {{ kanbanStats.unassigned }}
          </p>
        </div>
        <div
          class="flex items-center gap-2 h-8 px-2.5 border border-solid rounded-md border-n-weak bg-n-alpha-1"
        >
          <p class="m-0 text-xs text-n-slate-10">Ativos 24h</p>
          <p class="m-0 text-sm font-semibold text-n-blue-10">
            {{ kanbanStats.today }}
          </p>
        </div>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <label
          class="flex flex-none items-center gap-2 h-9 px-3 py-0 border border-solid rounded-lg !w-80 max-w-full bg-n-background border-n-weak"
        >
          <i class="i-lucide-search text-n-slate-10" />
          <input
            v-model="searchQuery"
            type="search"
            class="w-full p-0 text-sm bg-transparent border-0 outline-none text-n-slate-12 placeholder:text-n-slate-10"
            placeholder="Buscar cliente, agente ou etiqueta"
          />
        </label>

        <select
          v-model="selectedAgent"
          class="h-9 px-3 text-sm border border-solid rounded-lg !w-48 flex-none bg-n-background border-n-weak text-n-slate-12"
        >
          <option value="all">Todos os agentes</option>
          <option value="unassigned">Sem agente</option>
          <option
            v-for="agent in agentOptions"
            :key="agent.id"
            :value="agent.id"
          >
            {{ agent.name }}
          </option>
        </select>

        <select
          v-model="selectedLabel"
          class="h-9 px-3 text-sm border border-solid rounded-lg !w-52 flex-none bg-n-background border-n-weak text-n-slate-12"
        >
          <option value="all">Todas as etiquetas</option>
          <option
            v-for="label in usedLabelOptions"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>

        <select
          v-model="selectedActivity"
          class="h-9 px-3 text-sm border border-solid rounded-lg !w-44 flex-none bg-n-background border-n-weak text-n-slate-12"
        >
          <option value="all">Qualquer período</option>
          <option value="today">Ativos em 24h</option>
          <option value="recent">Até 3 dias</option>
          <option value="old">Mais antigos</option>
        </select>

        <label
          class="inline-flex items-center gap-2 h-9 px-3 text-sm border border-solid rounded-lg cursor-pointer select-none bg-n-background border-n-weak text-n-slate-12"
        >
          <input v-model="unreadOnly" type="checkbox" class="m-0" />
          Só não lidas
        </label>

        <Button
          label="Limpar"
          icon="i-lucide-x"
          slate
          faded
          sm
          @click="resetFilters"
        />
      </div>

      <div class="flex items-center gap-2 pb-1 overflow-x-auto">
        <div
          v-for="stage in orderedStageOptions"
          :key="`summary-${stage.id}`"
          class="flex flex-none items-center gap-2 px-2.5 py-1 border border-solid rounded-md bg-n-alpha-1 border-n-weak"
        >
          <span
            class="w-1 h-6 rounded-full"
            :style="{ backgroundColor: stageColorMap[stage.id] }"
          />
          <span class="text-xs font-medium text-n-slate-12">
            {{ formatConversationStageLabel(stage.name) }}
          </span>
          <span class="text-xs font-semibold text-n-slate-12">
            {{ stageMetrics(stage.id).total }}
          </span>
          <span class="text-[11px] text-n-slate-10">
            {{ stagePercentFor(stage.id) }}%
          </span>
        </div>
      </div>
    </header>

    <section class="flex-1 min-h-0 p-3 overflow-x-auto bg-n-alpha-1">
      <div class="grid h-full gap-3 auto-cols-[19rem] grid-flow-col">
        <section
          v-for="stage in orderedStageOptions"
          :key="stage.id"
          class="flex flex-col min-h-0 overflow-hidden border border-solid rounded-lg bg-n-surface-1 border-n-weak"
          :class="{
            'outline outline-2 outline-n-blue-8 border-n-blue-8':
              activeDropStage === stage.id,
          }"
          @dragenter.prevent="activeDropStage = stage.id"
          @dragover.prevent
          @dragleave="activeDropStage = ''"
          @drop="moveConversation(stage.id)"
        >
          <header class="px-3 py-2 border-b border-solid border-n-weak">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center min-w-0 gap-2">
                <span
                  class="w-1.5 h-7 rounded-full"
                  :style="{ backgroundColor: stageColorMap[stage.id] }"
                />
                <div class="min-w-0">
                  <h2
                    class="m-0 text-sm font-semibold text-n-slate-12 truncate"
                  >
                    {{ formatConversationStageLabel(stage.name) }}
                  </h2>
                  <p class="m-0 text-[11px] text-n-slate-10">
                    {{ stageMetrics(stage.id).unread }} msg ·
                    {{ stageMetrics(stage.id).unassigned }} sem agente ·
                    {{ stagePercentFor(stage.id) }}%
                  </p>
                </div>
              </div>
              <span
                class="px-2 py-1 text-xs font-semibold rounded-md text-n-slate-12 bg-n-alpha-2"
              >
                {{ stageMetrics(stage.id).total }}
              </span>
            </div>
          </header>

          <div class="flex-1 min-h-0 p-2 space-y-1.5 overflow-y-auto">
            <article
              v-for="conversation in conversationsByStage[stage.id]"
              :key="conversation.id"
              draggable="true"
              class="relative p-3 transition border border-solid rounded-md cursor-grab bg-n-background border-n-weak hover:border-n-blue-7 hover:shadow-sm active:cursor-grabbing"
              @dragstart="draggedConversationId = conversation.id"
              @dragend="draggedConversationId = null"
              @click="openConversation(conversation)"
            >
              <div class="flex items-start justify-between gap-2">
                <p
                  class="m-0 text-[13px] leading-4 font-semibold text-n-slate-12 line-clamp-2"
                >
                  {{ conversationName(conversation) }}
                </p>
                <span
                  v-if="conversation.unread_count"
                  class="grid flex-shrink-0 h-5 min-w-5 px-1 text-[11px] font-semibold text-white rounded-full place-items-center bg-n-teal-9"
                >
                  {{ conversation.unread_count }}
                </span>
              </div>

              <div class="flex items-center justify-between gap-2 mt-1">
                <span class="text-[11px] truncate text-n-slate-10">
                  {{ conversationAgent(conversation) }}
                </span>
                <span class="text-[11px] flex-shrink-0 text-n-slate-10">
                  {{
                    relativeActivityLabel(conversationTimestamp(conversation))
                  }}
                </span>
              </div>

              <div
                v-if="cardLabelsFor(conversation).length"
                class="flex flex-wrap gap-1 mt-2"
              >
                <span
                  v-for="label in cardLabelsFor(conversation)"
                  :key="label.id"
                  class="max-w-full px-1.5 py-0.5 text-[10px] font-medium rounded text-n-slate-12 truncate"
                  :style="{ backgroundColor: `${label.color}24` }"
                >
                  {{ label.title }}
                </span>
              </div>
            </article>

            <div
              v-if="!conversationsByStage[stage.id]?.length"
              class="grid p-4 text-xs text-center border border-dashed rounded-lg min-h-24 place-items-center text-n-slate-10 border-n-weak"
            >
              Nenhuma conversa nesta etapa
            </div>
          </div>
        </section>
      </div>
    </section>
  </main>
</template>

<style scoped>
select {
  width: auto;
  min-width: 0;
}
</style>
